/* eslint-disable @typescript-eslint/no-explicit-any */
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { PaginatedResponse } from "@/types/common";
import { TemplateType } from "@/types/template.types";
import { Code, Text } from "lucide-react";
import TemplateActions from "./tempalte-actions";
import { cn } from "@/lib/utils";

type Props = {
  templates: PaginatedResponse<TemplateType> | undefined;
  isLoading: boolean;
};

const TemplateGridView = ({ templates, isLoading }: Props) => {

  const renderContent = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {templates?.data?.map((template) => (
        <Card
          key={template._id}
          className="col-span-1 p-3 shadow-none group hover:bg-muted/40"
        >
          <CardHeader className="p-0 flex flex-row space-y-0 items-start justify-between mb-2">
            <CardTitle className="line-clamp-2">{template.title}</CardTitle>

            <TemplateActions data={template} type="grid" />
          </CardHeader>

          <CardContent className="p-0 text-sm">
            <div className="mb-2">
              <span className="font-medium">Subject: </span>
              <span>{template.subject}</span>
            </div>
            <p className="opacity-80 h-[100px] overflow-hidden text-ellipsis">
              {template.content}
            </p>
            <Separator className="my-2" />
            <div className="flex items-center justify-end gap-2 text-xs">
              <div className="flex items-center gap-3">
                {template.reportId?.report?.score && (
                  <div
                    className={cn(
                      "size-5 rounded-full text-xs flex items-center justify-center text-black font-semibold ring-2 ring-offset-2",
                      (template.reportId?.report?.score || 0) >= 90 &&
                        "bg-green-200 ring-green-400",
                      (template.reportId?.report?.score || 0) >= 70 &&
                        (template.reportId?.report?.score || 0) < 90 &&
                        "bg-yellow-200 ring-yellow-400",
                      (template.reportId?.report?.score || 0) < 70 &&
                        "bg-red-200 ring-red-400",
                    )}
                  >
                    <span>
                      {Math.round(template.reportId?.report?.score || 0)}
                    </span>
                  </div>
                )}
                <Badge className="text-accent-foreground bg-accent hover:bg-accent shadow-none rounded-sm space-x-2">
                  {template.contentType === "html" ? (
                    <>
                      <Code size={12} />
                      <span>HTML</span>
                    </>
                  ) : (
                    <>
                      <Text size={12} />
                      <span>Plain Text</span>
                    </>
                  )}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const renderSkeleton = () => {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 12 }).map((_, index) => (
          <Skeleton key={index} className="h-[250px] w-full col-span-1" />
        ))}
      </div>
    );
  };

  const renderEmpty = () => {
    return (
      <div className="flex items-center justify-center h-full">
        <span>No templates found</span>
      </div>
    );
  };

  return (
    <div>
      {isLoading ? renderSkeleton() : renderContent()}
      {templates?.data.length === 0 && renderEmpty()}
    </div>
  );
};

export default TemplateGridView;
